import { useState, useEffect, useCallback } from 'react';
import { useUser } from '@clerk/clerk-react';
import { authenticatedFetch } from '../services/apiClient.js';

export function useSubscription() {
  const { user, isLoaded } = useUser();
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkSubscription = useCallback(async () => {
    try {
      const email = user?.primaryEmailAddress?.emailAddress;
      if (!email) {
        setIsLoading(false);
        return;
      }

      // authenticatedFetch attaches Authorization: Bearer <Clerk JWT>.
      // The endpoint requires it and identifies the user by clerk_id —
      // email in the body is now only used as a fallback when the
      // Supabase row is missing and we have to do a Stripe lookup.
      const response = await authenticatedFetch('/api/check-subscription', {
        method: 'POST',
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error(`check-subscription returned ${response.status}`);
      }

      const data = await response.json();
      setSubscription(data);
    } catch (err) {
      console.error('[useSubscription] Error:', err);
      // Fail-closed: if we can't verify subscription status, deny paid
      // access. Free Days 1-5 still work because the Week-2+ guard in
      // DayDetail reads isActive, which now defaults to false on error
      // rather than true — meaning a transient network failure on
      // /api/check-subscription temporarily locks paying users out of
      // Week 2+ until the next successful check. That's the right
      // trade-off vs. anyone with dev tools getting permanent free
      // access by blocking the endpoint.
      setSubscription({ status: 'unknown', isActive: false });
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!isLoaded) return;
    if (!user) {
      setIsLoading(false);
      return;
    }
    checkSubscription();
  }, [user, isLoaded, checkSubscription]);

  return {
    subscription,
    isLoading,
    isActive: subscription?.isActive || false,
    isTrial: subscription?.isTrial || false,
    status: subscription?.status || 'none',
    refresh: checkSubscription,
  };
}
